import type { LocationType } from './dashboard'
import type { SchedulableLocationType } from './schedule'

export type HuntType =
  | 'migratory_bird'
  | 'deer'
  | 'turkey'
  | 'gator'
  | 'hog'
  | 'upland'

export type HuntTimeSlot = 'am' | 'midday' | 'pm' | 'all-day' | 'night'

export const HUNT_TYPES: HuntType[] = [
  'migratory_bird',
  'deer',
  'turkey',
  'gator',
  'hog',
  'upland',
]

const HUNT_TYPE_LABELS: Record<HuntType, string> = {
  migratory_bird: 'Migratory Birds',
  deer: 'Deer',
  turkey: 'Turkey',
  gator: 'Gator',
  hog: 'Hog',
  upland: 'Upland',
}

export function huntTypeLabel(huntType: HuntType): string {
  return HUNT_TYPE_LABELS[huntType]
}

export function huntTypeFromWire(value: string | null | undefined): HuntType {
  if (!value) return 'migratory_bird'
  const match = HUNT_TYPES.find((type) => type === value)
  return match ?? 'migratory_bird'
}

export function isMigratory(huntType: HuntType): boolean {
  return huntType === 'migratory_bird'
}

export function isBigGame(huntType: HuntType): boolean {
  return (
    huntType === 'deer' || huntType === 'turkey' || huntType === 'gator'
  )
}

export function isDeer(huntType: HuntType): boolean {
  return huntType === 'deer'
}

export function isTurkey(huntType: HuntType): boolean {
  return huntType === 'turkey'
}

export function usesStands(huntType: HuntType): boolean {
  return isDeer(huntType) || huntType === 'turkey'
}

export function usesBlinds(huntType: HuntType): boolean {
  return allowedLocationKinds(huntType).has('blind')
}

export function isUpland(huntType: HuntType): boolean {
  return huntType === 'upland'
}

export function supportsDogs(huntType: HuntType | undefined): boolean {
  return huntType === 'upland' || huntType === 'migratory_bird'
}

export function isCountSheet(huntType: HuntType): boolean {
  return (
    huntType === 'migratory_bird' ||
    huntType === 'upland' ||
    huntType === 'hog'
  )
}

export function isSingleSpecies(huntType: HuntType): boolean {
  return huntType === 'hog'
}

export function harvestContainerLabel(huntType: HuntType): string {
  switch (huntType) {
    case 'migratory_bird':
    case 'upland':
      return 'Birds'
    case 'deer':
      return 'Deer'
    case 'turkey':
      return 'Turkeys'
    case 'gator':
      return 'Gators'
    case 'hog':
      return 'Hogs'
  }
}

export function harvestSingularLabel(huntType: HuntType): string {
  switch (huntType) {
    case 'migratory_bird':
    case 'upland':
      return 'Bird'
    case 'deer':
      return 'Deer'
    case 'turkey':
      return 'Turkey'
    case 'gator':
      return 'Gator'
    case 'hog':
      return 'Hog'
  }
}

export function harvestRateLabel(huntType: HuntType): string {
  switch (huntType) {
    case 'migratory_bird':
    case 'upland':
      return 'B/H/H'
    case 'deer':
      return 'D/H/H'
    case 'turkey':
      return 'T/H/H'
    case 'gator':
      return 'G/H/H'
    case 'hog':
      return 'H/H/H'
  }
}

export function harvestRateFullLabel(huntType: HuntType): string {
  switch (huntType) {
    case 'migratory_bird':
    case 'upland':
      return 'Birds per Hunter per Hunt'
    case 'deer':
      return 'Deer per Hunter per Hunt'
    case 'turkey':
      return 'Turkeys per Hunter per Hunt'
    case 'gator':
      return 'Gators per Hunter per Hunt'
    case 'hog':
      return 'Hogs per Hunter per Hunt'
  }
}

export function locationRowNoun(locationType: LocationType | undefined): string {
  switch (locationType) {
    case 'stand':
      return 'stand'
    case 'custom_location':
      return 'spot'
    default:
      return 'blind'
  }
}

export function locationUnitLabel(huntType: HuntType): string {
  switch (huntType) {
    case 'deer':
    case 'turkey':
      return 'Stand'
    case 'upland':
      return 'Field'
    case 'gator':
      return 'Waterbody'
    case 'hog':
      return 'Property'
    case 'migratory_bird':
      return 'Blind'
  }
}

export function locationUnitLabelPlural(huntType: HuntType): string {
  switch (huntType) {
    case 'deer':
    case 'turkey':
      return 'Stands'
    case 'upland':
      return 'Fields'
    case 'gator':
      return 'Waterbodies'
    case 'hog':
      return 'Properties'
    case 'migratory_bird':
      return 'Blinds'
  }
}

export function locationPerformanceTitle(huntType: HuntType): string {
  return usesStands(huntType) ? 'Stand Performance' : 'Blind Performance'
}

export function locationToggleLabel(huntType: HuntType): string {
  return usesStands(huntType) ? 'Stands' : 'Blinds'
}

export function fieldOrZoneLabel(huntType: HuntType): string {
  return isUpland(huntType) ? 'Field' : 'Zone'
}

export type LocationKind = 'blind' | 'stand' | 'zone' | 'custom'

export function allowedLocationKinds(huntType: HuntType): Set<LocationKind> {
  switch (huntType) {
    case 'migratory_bird':
      return new Set(['blind', 'zone', 'custom'])
    case 'deer':
      return new Set(['stand', 'zone', 'custom'])
    case 'turkey':
      return new Set(['blind', 'stand', 'zone', 'custom'])
    case 'upland':
      return new Set(['blind', 'zone', 'custom'])
    case 'hog':
      return new Set(['blind', 'zone', 'custom'])
    case 'gator':
      return new Set(['custom'])
  }
}

export function defaultLocationKind(huntType: HuntType): LocationKind {
  if (isUpland(huntType)) return 'zone'
  if (isDeer(huntType)) return 'stand'
  if (huntType === 'gator') return 'custom'
  return 'blind'
}

export function locationTypeForKind(kind: LocationKind): SchedulableLocationType {
  return kind === 'custom' ? 'custom_location' : kind
}

export type HuntTypeSetting = {
  huntType: HuntType
  isAllowed: boolean
  locationReservationEnabled: boolean
  multiDayEnabled: boolean
}

export function locationColumnHeader(huntType: HuntType): string {
  return usesStands(huntType) ? 'Stand' : 'Blind'
}

export function locationEmptyHeading(huntType: HuntType): string {
  if (isUpland(huntType)) return 'No field data'
  return usesStands(huntType) ? 'No stand data' : 'No blind data'
}

export function locationEmptyDescription(huntType: HuntType): string {
  if (isUpland(huntType)) {
    return 'No field performance data is available.'
  }
  return usesStands(huntType)
    ? 'No stand or zone performance data is available.'
    : 'No blind or zone performance data is available.'
}

export function allowedTimeSlots(huntType: HuntType): HuntTimeSlot[] {
  switch (huntType) {
    case 'gator':
    case 'hog':
      return ['am', 'midday', 'pm', 'all-day', 'night']
    case 'migratory_bird':
    case 'deer':
    case 'turkey':
    case 'upland':
      return ['am', 'midday', 'pm', 'all-day']
  }
}

export function allowedMethods(huntType: HuntType): string[] {
  switch (huntType) {
    case 'migratory_bird':
      return []
    case 'upland':
      return ['shotgun', 'other']
    case 'deer':
      return ['rifle', 'shotgun', 'bow', 'crossbow', 'muzzleloader', 'handgun']
    case 'turkey':
      return ['shotgun', 'bow', 'crossbow']
    case 'gator':
      return ['hook_and_line', 'harpoon', 'bow', 'bang_stick', 'firearm']
    case 'hog':
      return ['rifle', 'shotgun', 'bow', 'crossbow', 'handgun', 'other']
  }
}

export function allowedAgeClasses(huntType: HuntType): string[] {
  switch (huntType) {
    case 'deer':
      return ['fawn', 'yearling', '2.5', '3.5', '4.5_plus']
    case 'turkey':
    case 'upland':
      return ['adult', 'juvenile']
    case 'migratory_bird':
    case 'gator':
    case 'hog':
      return []
  }
}

export function sexVernacular(huntType: HuntType): {
  male: string
  female: string
} {
  switch (huntType) {
    case 'deer':
      return { male: 'Buck', female: 'Doe' }
    case 'turkey':
      return { male: 'Tom', female: 'Hen' }
    case 'gator':
      return { male: 'Bull', female: 'Female' }
    case 'hog':
      return { male: 'Boar', female: 'Sow' }
    case 'upland':
      return { male: 'Rooster', female: 'Hen' }
    case 'migratory_bird':
      return { male: 'Drake', female: 'Hen' }
  }
}

export function harvestActionLabel(huntType: HuntType): string {
  return huntType === 'hog' ? 'Removal' : 'Harvest'
}

export function harvestActionLabelPlural(huntType: HuntType): string {
  return huntType === 'hog' ? 'Removals' : 'Harvests'
}

export function humanizeOption(value: string): string {
  if (value === '4.5_plus') return '4.5+'
  if (value.includes('_')) {
    return value
      .split('_')
      .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : word))
      .join(' ')
  }
  if (!value) return value
  return value[0].toUpperCase() + value.slice(1)
}
